import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// Types
export interface Room {
  id: string;
  floor_plan_id: string;
  name: string;
  class: string;
  room_type?: string;
  confidence: number;
  x_position: number;
  y_position: number;
  width: number;
  height: number;
  points: { x: number; y: number }[];
  segmentation_mask?: string;
  features?: string[];
  detection_id: string | null;
  created_at: string;
}

export interface CreateRoomParams {
  floor_plan_id: string;
  name: string;
  class?: string;
  points: { x: number; y: number }[];
}

// Get rooms for a floor plan
export const getRooms = async (floorPlanId: string) => {
  const client = await getAuthenticatedSupabaseClient();
  const { data, error } = await client.from('rooms').select('*').eq('floor_plan_id', floorPlanId);

  if (error) {
    logger.error('Error fetching rooms:', error);
    throw error;
  }

  return data || [];
};

// Detect rooms using AI
export const detectRooms = async (floorPlanId: string, imagePath: string) => {
  try {
    logger.log(`Calling detect-rooms edge function with floor plan ID: ${floorPlanId}`);

    const client = await getAuthenticatedSupabaseClient();

    // Get the full image URL from the file path using supabase storage
    const { data: urlData } = client.storage.from('floor_plans').getPublicUrl(imagePath);

    const imageUrl = urlData.publicUrl;
    logger.log(`Full image URL: ${imageUrl}`);

    if (!imageUrl) {
      logger.error('Failed to get public URL for image path:', imagePath);
      throw new Error('Failed to get public URL for floor plan image');
    }

    // Call the detect-rooms edge function (now using Gemini)
    // The Supabase client will automatically include the Clerk JWT token
    const { data, error } = await client.functions.invoke('detect-rooms', {
      body: { floorPlanId, imageUrl },
    });

    // Log the response for debugging
    logger.log('Gemini room detection response:', { data, error });

    // Log specific details about the response
    if (data) {
      logger.log('Detection details:', {
        success: data.success,
        roomCount: data.roomCount,
        totalDetected: data.totalDetected,
        failedCount: data.failedCount,
        rooms: data.rooms,
      });
    }

    if (error) {
      logger.error('Edge function error:', error);
      throw error;
    }

    // Check if the response indicates an error even with status 200
    if (data && data.success === false) {
      logger.error('Edge function returned error in data:', data.error);
      throw new Error(data.error || 'Unknown error in room detection');
    }

    return { data, error: null };
  } catch (error) {
    logger.error('Error detecting rooms:', error);
    return { data: null, error };
  }
};

// Create a room manually
export const createRoom = async (params: CreateRoomParams) => {
  try {
    // Calculate bounding box based on points
    const xValues = params.points.map(p => p.x);
    const yValues = params.points.map(p => p.y);

    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    const width = maxX - minX;
    const height = maxY - minY;
    const x_position = minX + width / 2;
    const y_position = minY + height / 2;

    const client = await getAuthenticatedSupabaseClient();
    const { data, error } = await client
      .from('rooms')
      .insert({
        floor_plan_id: params.floor_plan_id,
        name: params.name,
        class: params.class || 'manual',
        confidence: 1, // Manually created rooms have 100% confidence
        x_position,
        y_position,
        width,
        height,
        points: params.points,
        detection_id: null, // No detection ID for manually created rooms
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    logger.error('Error creating room:', error);
    return { data: null, error };
  }
};

// Update a room
export const updateRoom = async (roomId: string, updates: Partial<Room>) => {
  try {
    // If points are updated, recalculate the bounding box
    if (updates.points) {
      const xValues = updates.points.map(p => p.x);
      const yValues = updates.points.map(p => p.y);

      const minX = Math.min(...xValues);
      const maxX = Math.max(...xValues);
      const minY = Math.min(...yValues);
      const maxY = Math.max(...yValues);

      updates.width = maxX - minX;
      updates.height = maxY - minY;
      updates.x_position = minX + updates.width / 2;
      updates.y_position = minY + updates.height / 2;
    }

    const client = await getAuthenticatedSupabaseClient();
    const { data, error } = await client
      .from('rooms')
      .update(updates)
      .eq('id', roomId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    logger.error('Error updating room:', error);
    return { data: null, error };
  }
};

// Delete a room
export const deleteRoom = async (roomId: string) => {
  try {
    const client = await getAuthenticatedSupabaseClient();
    const { error } = await client.from('rooms').delete().eq('id', roomId);

    if (error) {
      throw error;
    }

    return { success: true, error: null };
  } catch (error) {
    logger.error('Error deleting room:', error);
    return { success: false, error };
  }
};
